import { computed, defineComponent, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useDateFormat } from '@/composables/dateFormat';
import { ActivityMemoAction, ActivityMemoGetter } from '@/store/activityMemo/activityMemo.module';
import { ActivityAttachment, ActivityMemo } from '@/store/activityMemo/activityMemo.model';
import LoadingComponent from '@/components/loading/Loading.vue';
import PaginateComponent from '@/components/paginate/Paginate.vue';
import ActivityMemoFormComponent from './activityMemoForm/ActivityMemoForm.vue';
import ActivityMemoResultFormComponent from './activityMemoResultForm/ActivityMemoResultForm.vue';
import ActivityAttachmentFileModalComponent from './activityAttachmentFileModal/ActivityAttachmentFileModal.vue';
import ActivityAttachmentUploadModalComponent from './activityAttachmentFileModal/activityAttachmentUploadModal/ActivityAttachmentUploadModal.vue';
import ActivityAttachmentImageModalComponent from './activityAttachmentImageModal/ActivityAttachmentImageModal.vue';
import ActivityAttachmentOffcanvasConponent from './activityAttachmentOffcanvas/ActivityAttachmentOffcanvas.vue';

export default defineComponent({
  name: 'ActivityMemoListComponent',
  components: {
    LoadingComponent,
    PaginateComponent,
    ActivityMemoFormComponent,
    ActivityMemoResultFormComponent,
    ActivityAttachmentFileModalComponent,
    ActivityAttachmentUploadModalComponent,
    ActivityAttachmentImageModalComponent,
    ActivityAttachmentOffcanvasConponent,
  },
  props: {
    activityId: {
      type: Number,
      required: true,
    },
    provinceId: {
      type: Number,
      required: true,
    }
  },
  setup: (props) => {
    const store = useStore();
    const dateFormat = useDateFormat();
    const activityMemos = computed<ActivityMemo[]>(() => store.getters[ActivityMemoGetter.ALL]);
    const isLoading = computed(() => store.getters[ActivityMemoGetter.STATUS] === 'init' || store.getters[ActivityMemoGetter.STATUS] === 'loading');
    const search = computed(() => activityMemos.value.filter((activityMemo) => toSearch(activityMemo)));
    const display = computed(() => search.value.filter((_, index) => toDisplay(index)));
    const activityMemoForm = ref<InstanceType<typeof ActivityMemoFormComponent>>();
    const activityMemoResultForm = ref<InstanceType<typeof ActivityMemoResultFormComponent>>();
    const activityAttachmentFileModal = ref<InstanceType<typeof ActivityAttachmentFileModalComponent>>();
    const activityAttachmentUploadModal = ref<InstanceType<typeof ActivityAttachmentUploadModalComponent>>();
    const activityAttachmentImageModal = ref<InstanceType<typeof ActivityAttachmentImageModalComponent>>();
    const activityAttachmentOffcanvas = ref<InstanceType<typeof ActivityAttachmentOffcanvasConponent>>();
    const activityMemoId = ref<number | null>(null);
    const activityAttachments = ref<ActivityAttachment[]>([]);

    const filter = reactive({
      keyword: '',
      amount: computed(() => search.value.length),
      total: computed(() => activityMemos.value.length)
    });

    const page = reactive({
      size: 6,
      current: 1
    });

    onMounted(() => {
      fetchData(props.activityId, props.provinceId);
    });

    watch(() => ([props.activityId, props.provinceId]), ([activityId, provinceId]) => {
      fetchData(activityId, provinceId);
    });

    const fetchData = (activityId: number, provinceId: number) => {
      store.dispatch(ActivityMemoAction.FETCH, { activityId: activityId, provinceId: provinceId });
    };

    const toSearch = (activityMemo: ActivityMemo): ActivityMemo | boolean => {
      return (filter.keyword)
        ? activityMemo.subject.toLowerCase().includes(filter.keyword.toLowerCase())
          ? activityMemo
          : false
        : true;
    };

    const toDisplay = (index: number) => {
      const start = (page.current - 1) * page.size;
      const end = start + page.size;
      return index >= start && index < end;
    };

    const createClick = () => {
      Promise.resolve(activityMemoId.value = null)
        .then(() => activityMemoForm.value?.show());
    };

    const editClick = (id: number) => {
      Promise.resolve(activityMemoId.value = id)
        .then(() => activityMemoForm.value?.show());
    };

    const resultClick = (id: number) => {
      Promise.resolve(activityMemoId.value = id)
        .then(() => activityMemoResultForm.value?.show());
    };

    const attachmentFileClick = (id: number) => {
      Promise.resolve(activityMemoId.value = id)
        .then(() => activityAttachmentFileModal.value?.show());
    };

    const handleUpload = () => {
      activityAttachmentUploadModal.value?.show();
    };

    const handleHidden = () => {
      activityAttachmentFileModal.value?.show();
    };

    const attachmentImageClick = (id: number) => {
      Promise.resolve(activityMemoId.value = id)
        .then(() => activityAttachmentImageModal.value?.show());
    };

    const showAttachment = (attachments: ActivityAttachment[]) => {
      Promise.resolve(activityAttachments.value = attachments)
        .then(() => activityAttachmentOffcanvas.value?.show());
    };

    const selectedPage = (pageNumber: number) => {
      page.current = pageNumber;
    };

    const thaiDate = (date: Date) =>
      dateFormat.toThaiDate(date, 'long');

    return {
      isLoading,
      display,
      filter,
      page,
      activityMemoForm,
      activityMemoResultForm,
      activityAttachmentFileModal,
      activityAttachmentUploadModal,
      activityAttachmentImageModal,
      activityAttachmentOffcanvas,
      activityMemoId,
      activityAttachments,
      createClick,
      editClick,
      resultClick,
      selectedPage,
      attachmentFileClick,
      attachmentImageClick,
      handleUpload,
      handleHidden,
      showAttachment,
      thaiDate,
    };
  },
});
